import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, CheckCircle2, AlertTriangle, Camera } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuditItems, useAudit, signedAuditPhotoUrl } from "@/features/audits/api";
import { ReconcileDialog } from "@/features/audits/ReconcileDialog";
import { DocumentViewerDialog } from "@/components/documents/DocumentViewerDialog";
import { toast } from "sonner";
import { ConditionBadge, DiscrepancyBadge } from "@/features/audits/badges";
import type { AuditItemRow } from "@/features/audits/types";
import { formatRoles } from "@/lib/roles";

export const Route = createFileRoute("/_authenticated/audits/$auditId/discrepancies")({
  component: AuditDiscrepancies,
});

function AuditDiscrepancies() {
  const { auditId } = Route.useParams();
  const { data: audit } = useAudit(auditId);
  const { data: items = [], isLoading } = useAuditItems(auditId, { onlyDiscrepancies: true });
  const [target, setTarget] = useState<AuditItemRow | null>(null);
  const [photoViewer, setPhotoViewer] = useState<string | null>(null);
  const isLocked = audit?.status === "Completed" || audit?.status === "Signed Off";

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">Discrepancies ({items.length})</h2>

      {isLocked && (
        <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>This audit is <strong>{audit?.status}</strong> and is locked. You cannot reconcile discrepancies.</div>
        </div>
      )}

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        items.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No discrepancies recorded.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Asset</TableHead><TableHead>Expected</TableHead><TableHead>Found</TableHead>
              <TableHead>Condition</TableHead><TableHead>Type</TableHead>
              <TableHead>Verified By</TableHead><TableHead>Photo</TableHead><TableHead>Reconciliation</TableHead>
              <TableHead>Assigned To</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell className="text-sm">
                    {it.is_untagged_stub ? (
                      <div className="flex flex-col gap-1">
                        <div className="italic text-muted-foreground">Untagged: {it.untagged_description}</div>
                      </div>
                    ) : (
                      <>
                        <div className="font-mono text-xs">{it.asset?.asset_code}</div>
                        <div className="text-xs text-muted-foreground">{it.asset?.name}</div>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.expected_location?.name ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.found_location?.name ?? "—"}</TableCell>
                  <TableCell><ConditionBadge condition={it.condition} /></TableCell>
                  <TableCell><DiscrepancyBadge type={it.discrepancy_type} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.verifier?.full_name ?? it.verifier?.email ?? "—"}</TableCell>
                  <TableCell>
                    {it.photo_url ? (
                      <button 
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const url = await signedAuditPhotoUrl(it.photo_url!);
                            setPhotoViewer(url);
                          } catch (err) {
                            toast.error("Failed to load photo");
                          }
                        }} 
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <Camera className="h-3 w-3" /> View
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {it.reconciled_at ? (
                      <div className="flex items-start gap-1 text-emerald-700">
                        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <div>{it.reconciliation_action}</div>
                      </div>
                    ) : <span className="text-muted-foreground">Open</span>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {it.responsible ? (
                      <div>
                        <div className="font-medium text-foreground">
                          {(it.responsible as any).full_name || (it.responsible as any).username || it.responsible.email}
                        </div>
                        {(it.responsible as any).roles?.length > 0 && (
                          <div className="text-[10px]">{formatRoles((it.responsible as any).roles)}</div>
                        )}
                      </div>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" onClick={() => setTarget(it)} disabled={isLocked}>
                      {it.reconciled_at ? "Update" : "Reconcile"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <ReconcileDialog item={target} auditId={auditId} open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }} />
      <DocumentViewerDialog
        open={!!photoViewer}
        onOpenChange={(o) => { if (!o) setPhotoViewer(null); }}
        bucket="asset-docs"
        path={photoViewer}
        fileName="photo.jpg"
      />
    </div>
  );
}
